/**
 * Chat page — AI assistant with conversation history and Excel context.
 *
 * Enhancements over basic chat:
 *   - Automatically reads Excel context on every send (sheet name, selection, formula).
 *   - "Context" button inserts a structured preview into the message input.
 *   - Tracks interactions in the history store.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button, Textarea, Text, Badge, Tooltip } from '@fluentui/react-components';
import { SendRegular, DocumentTableRegular, DeleteRegular } from '@fluentui/react-icons';
import type { ChatMessage as ChatMessageType } from '@excel-ai-studio/shared';
import { sendChat } from '../services/api.client';
import { readExcelContext } from '../office';
import { getSettings } from '../store/settings.store';
import { addHistoryEntry } from '../store/history.store';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { FormulaCard } from '../components/features/FormulaCard';
import { ChatBubble } from '../components/features/ChatBubble';

export const ChatPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [lastFormula, setLastFormula] = useState<string>();
  const [contextLoaded, setContextLoaded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    const startedAt = Date.now();

    setError(undefined);
    setLastFormula(undefined);

    const userMessage: ChatMessageType = { role: 'user', content: trimmed, timestamp: Date.now() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);
    setContextLoaded(false);

    const settings = getSettings();

    try {
      let excelContext;
      try {
        excelContext = await readExcelContext();
      } catch {
        // Not in Excel environment
      }

      const response = await sendChat({
        messages: updatedMessages,
        excelContext,
      });

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: response.content, timestamp: Date.now() },
      ]);

      if (response.formula) {
        setLastFormula(response.formula);
      }

      addHistoryEntry(
        'chat',
        settings.provider,
        settings.model,
        trimmed,
        true,
        undefined,
        {
          module: 'assistant',
          durationMs: Date.now() - startedAt,
          sheetName: excelContext?.sheetName,
          rangeAddress: excelContext?.selectedRange,
        },
      );
    } catch (err) {
      setError((err as Error).message);
      addHistoryEntry(
        'chat',
        settings.provider,
        settings.model,
        trimmed,
        false,
        (err as Error).message,
        {
          module: 'assistant',
          durationMs: Date.now() - startedAt,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInsertContext = async () => {
    try {
      const ctx = await readExcelContext();
      const parts: string[] = [];
      if (ctx.sheetName) parts.push(`Feuille: ${ctx.sheetName}`);
      if (ctx.selectedRange) parts.push(`Selection: ${ctx.selectedRange}`);
      if (ctx.activeCellFormula) parts.push(`Formule active: ${ctx.activeCellFormula}`);
      if (ctx.activeCellValue != null) parts.push(`Valeur active: ${String(ctx.activeCellValue)}`);
      if (ctx.headers) parts.push(`En-tetes: ${ctx.headers.join(', ')}`);

      // Include a small data preview (first 5 rows x first 5 cols)
      if (ctx.selectedValues && ctx.selectedValues.length > 0) {
        const previewRows = ctx.selectedValues.slice(0, 6); // headers + 5 data rows
        const previewCols = 5;
        const preview = previewRows
          .map((row) =>
            (row as unknown[]).slice(0, previewCols).map((c) => c == null ? '' : String(c)).join(' | '),
          )
          .join('\n');
        parts.push(`Apercu des donnees:\n${preview}`);
        if (ctx.selectedValues.length > 6) {
          parts.push(`... (${ctx.selectedValues.length - 6} lignes supplementaires)`);
        }
      }

      if (parts.length > 0) {
        setInput((prev) => prev + (prev ? '\n\n' : '') + '[Contexte Excel]\n' + parts.join('\n'));
        setContextLoaded(true);
      }
    } catch {
      setError("Impossible de lire le contexte Excel.");
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    setLastFormula(undefined);
    setError(undefined);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-secondary)' }}>
            <Text size={400} weight="semibold" block>
              Bienvenue dans Excel AI Studio
            </Text>
            <Text size={300} block style={{ marginTop: 4 }}>
              Posez une question, demandez une formule, ou analysez vos donnees.
            </Text>
            <Text size={200} block style={{ marginTop: 8, fontStyle: 'italic' }}>
              Astuce : utilisez le bouton &laquo;Contexte&raquo; pour injecter automatiquement
              la selection Excel dans votre message.
            </Text>
          </div>
        )}

        {messages.map((msg, idx) => (
          <ChatBubble key={idx} message={msg} />
        ))}

        {loading && <LoadingSpinner label="Reflexion en cours..." />}
        {error && <ErrorDisplay message={error} onDismiss={() => setError(undefined)} />}
        {lastFormula && <FormulaCard formula={lastFormula} />}

        <div ref={messagesEndRef} />
      </div>

      <div style={{ borderTop: '1px solid var(--border-color)', padding: '8px 0 0', flexShrink: 0 }}>
        <Textarea
          value={input}
          onChange={(_e, data) => setInput(data.value)}
          onKeyDown={handleKeyDown}
          placeholder="Demandez une formule, posez une question..."
          resize="vertical"
          style={{ width: '100%', minHeight: 60 }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 6, justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Tooltip content="Inserer le contexte Excel (selection, formule, en-tetes)" relationship="label">
              <Button size="small" icon={<DocumentTableRegular />} onClick={handleInsertContext}>
                Contexte
              </Button>
            </Tooltip>
            {contextLoaded && (
              <Badge size="small" appearance="tint" color="success">OK</Badge>
            )}
            {messages.length > 0 && (
              <Tooltip content="Effacer la conversation" relationship="label">
                <Button size="small" appearance="subtle" icon={<DeleteRegular />} onClick={handleClearChat} />
              </Tooltip>
            )}
          </div>
          <Button
            size="small"
            appearance="primary"
            icon={<SendRegular />}
            onClick={handleSend}
            disabled={!input.trim() || loading}
          >
            Envoyer
          </Button>
        </div>
      </div>
    </div>
  );
};
